import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Square, Circle, BookOpen, Layers } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ShapeSelectionSection from './ShapeSelectionSection';
import RectangleSection from './RectangleSection';
import CylinderSection from './CylinderSection';
import LShapeSection from './LShapeSection';
import ArbitraryShapeSection from './ArbitraryShapeSection';
import MaterialSection from './MaterialSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

/**
 * Компонент основной формы расчета объема бетона
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  projectTypes,
  concreteGrades,
  reinforcementTypes,
  regions,
  concretePriceBase,
  saveProject 
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    if (!val && val !== 0) return defaultVal;
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Сброс результатов при изменении формы
      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик добавления новой секции для произвольной формы
  const handleAddSection = () => {
    setFormData(prev => ({
      ...prev,
      customSections: [
        ...prev.customSections,
        { length: '1', width: '1', height: '0.15', quantity: 1 }
      ]
    }));
  };

  // Обработчик удаления секции для произвольной формы
  const handleRemoveSection = (index) => {
    setFormData(prev => {
      const updatedSections = [...prev.customSections];
      updatedSections.splice(index, 1);
      return {
        ...prev,
        customSections: updatedSections.length > 0 ? updatedSections : [{ length: '1', width: '1', height: '0.15', quantity: 1 }]
      };
    });
  };

  // Обработчик изменения полей секции для произвольной формы
  const handleSectionChange = (index, field, value) => {
    setFormData(prev => {
      const updatedSections = [...prev.customSections];
      updatedSections[index] = {
        ...updatedSections[index],
        [field]: value
      };
      return {
        ...prev,
        customSections: updatedSections
      };
    });
  };

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { shapeType, length, width, height, diameter, radius, thickness,
            lShapePrimaryLength, lShapePrimaryWidth, lShapeSecondaryLength, lShapeSecondaryWidth,
            customSections, wastagePercent } = formData;

    // Проверка значений в зависимости от типа формы
    if (shapeType === 'rectangle') {
      const numLength = parseFloatOrDefault(length, -1);
      const numWidth = parseFloatOrDefault(width, -1);
      const numHeight = parseFloatOrDefault(height, -1);

      if (numLength <= 0) newErrors.length = 'Длина должна быть положительным числом';
      if (numWidth <= 0) newErrors.width = 'Ширина должна быть положительным числом';
      if (numHeight <= 0) newErrors.height = 'Высота должна быть положительным числом';
    } 
    else if (shapeType === 'cylinder') {
      const numDiameter = parseFloatOrDefault(diameter, -1);
      const numHeight = parseFloatOrDefault(height, -1);
      const numRadius = parseFloatOrDefault(radius, -1);

      if (numDiameter <= 0 && numRadius <= 0) {
        newErrors.diameter = 'Укажите диаметр или радиус';
        newErrors.radius = 'Укажите диаметр или радиус';
      }
      if (numHeight <= 0) newErrors.height = 'Высота должна быть положительным числом';
    } 
    else if (shapeType === 'l_shape') {
      const numPrimaryLength = parseFloatOrDefault(lShapePrimaryLength, -1);
      const numPrimaryWidth = parseFloatOrDefault(lShapePrimaryWidth, -1);
      const numSecondaryLength = parseFloatOrDefault(lShapeSecondaryLength, -1);
      const numSecondaryWidth = parseFloatOrDefault(lShapeSecondaryWidth, -1);
      const numHeight = parseFloatOrDefault(height, -1);

      if (numPrimaryLength <= 0) newErrors.lShapePrimaryLength = 'Длина должна быть положительным числом';
      if (numPrimaryWidth <= 0) newErrors.lShapePrimaryWidth = 'Ширина должна быть положительным числом';
      if (numSecondaryLength <= 0) newErrors.lShapeSecondaryLength = 'Длина должна быть положительным числом';
      if (numSecondaryWidth <= 0) newErrors.lShapeSecondaryWidth = 'Ширина должна быть положительным числом';
      if (numHeight <= 0) newErrors.height = 'Высота должна быть положительным числом';
    } 
    else if (shapeType === 'arbitrary') {
      let hasErrors = false;
      customSections.forEach((section, index) => {
        const numLength = parseFloatOrDefault(section.length, -1);
        const numWidth = parseFloatOrDefault(section.width, -1);
        const numHeight = parseFloatOrDefault(section.height, -1);
        const numQuantity = parseInt(section.quantity, 10) || 0;

        if (numLength <= 0) {
          newErrors[`section_${index}_length`] = 'Длина должна быть положительным числом';
          hasErrors = true;
        }
        if (numWidth <= 0) {
          newErrors[`section_${index}_width`] = 'Ширина должна быть положительным числом';
          hasErrors = true;
        }
        if (numHeight <= 0) {
          newErrors[`section_${index}_height`] = 'Высота должна быть положительным числом';
          hasErrors = true;
        }
        if (numQuantity <= 0) {
          newErrors[`section_${index}_quantity`] = 'Количество должно быть положительным числом';
          hasErrors = true;
        }
      });

      if (hasErrors) {
        newErrors.customSections = 'Исправьте ошибки в размерах секций';
      }
    }

    // Проверка процента отходов
    const numWastagePercent = parseFloatOrDefault(wastagePercent, -1);
    if (numWastagePercent < 0) newErrors.wastagePercent = 'Процент отходов не может быть отрицательным';
    if (numWastagePercent > 50) newErrors.wastagePercent = 'Процент отходов не может быть более 50%';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет объема бетона
  const calculateConcreteVolume = useCallback(() => {
    if (!validateForm()) {
      // Если есть ошибки, фокусируемся на первом поле с ошибкой
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const { shapeType, length, width, height, diameter, radius, thickness,
                lShapePrimaryLength, lShapePrimaryWidth, lShapeSecondaryLength, lShapeSecondaryWidth,
                customSections, concreteGrade, reinforcementType, reinforcementDensity,
                includeWastage, wastagePercent, projectType, deliveryDistance,
                regionPrice, includeDelivery, includePump, includeLabor, laborCost } = formData;
                
        // Расчет базового объема бетона в зависимости от формы
        let baseVolume = 0;
        
        if (shapeType === 'rectangle') {
          // Прямоугольная форма (длина * ширина * высота)
          const numLength = parseFloatOrDefault(length);
          const numWidth = parseFloatOrDefault(width);
          const numHeight = parseFloatOrDefault(height);
          baseVolume = numLength * numWidth * numHeight;
        } 
        else if (shapeType === 'cylinder') {
          // Цилиндрическая форма (π * r² * высота)
          const numDiameter = parseFloatOrDefault(diameter);
          const numRadius = parseFloatOrDefault(radius);
          const numHeight = parseFloatOrDefault(height);
          
          // Используем радиус или диаметр/2
          const finalRadius = numRadius > 0 ? numRadius : numDiameter / 2;
          baseVolume = Math.PI * finalRadius * finalRadius * numHeight;
        } 
        else if (shapeType === 'l_shape') {
          // L-образная форма (два прямоугольника)
          const numPrimaryLength = parseFloatOrDefault(lShapePrimaryLength);
          const numPrimaryWidth = parseFloatOrDefault(lShapePrimaryWidth);
          const numSecondaryLength = parseFloatOrDefault(lShapeSecondaryLength);
          const numSecondaryWidth = parseFloatOrDefault(lShapeSecondaryWidth);
          const numHeight = parseFloatOrDefault(height);
          
          // Общий объем - прямоугольник основного сегмента + прямоугольник L-сегмента
          baseVolume = (numPrimaryLength * numPrimaryWidth * numHeight) + 
                       (numSecondaryLength * numSecondaryWidth * numHeight);
        } 
        else if (shapeType === 'arbitrary') {
          // Произвольная форма (сумма объемов секций)
          baseVolume = customSections.reduce((sum, section) => {
            const sectionLength = parseFloatOrDefault(section.length);
            const sectionWidth = parseFloatOrDefault(section.width);
            const sectionHeight = parseFloatOrDefault(section.height);
            const sectionQuantity = parseInt(section.quantity, 10) || 1;
            
            return sum + (sectionLength * sectionWidth * sectionHeight * sectionQuantity);
          }, 0);
        }
        
        // Расчет объема с учетом отходов
        const wasteMultiplier = includeWastage ? 1 + (parseFloatOrDefault(wastagePercent) / 100) : 1;
        const totalVolume = baseVolume * wasteMultiplier;
        
        // Расчет веса бетона (примерно 2400 кг/м³)
        const concreteWeight = totalVolume * 2400;
        
        // Расчет стоимости бетона
        const basePrice = concretePriceBase[concreteGrade] || 4000; // Цена за куб
        const selectedRegion = regions.find(r => r.id === regionPrice) || regions[0];
        const regionMultiplier = selectedRegion.priceModifier;
        
        // Базовая стоимость бетона
        let concreteCost = basePrice * totalVolume * regionMultiplier;
        
        // Стоимость доставки (примерно 150 руб за км за машину 7м³)
        const deliveryTruckCapacity = 7; // м³
        const deliveryPricePerKm = 150;
        const truckCount = Math.ceil(totalVolume / deliveryTruckCapacity);
        const deliveryCost = includeDelivery ? parseFloatOrDefault(deliveryDistance) * deliveryPricePerKm * truckCount : 0;
        
        // Стоимость подачи бетононасосом (примерно 2000 руб/час, минимум 4 часа)
        const pumpHourlyRate = 2000;
        const pumpMinHours = 4;
        // Примерно можно подать 15м³/час
        const pumpHours = Math.max(pumpMinHours, Math.ceil(totalVolume / 15));
        const pumpCost = includePump ? pumpHourlyRate * pumpHours : 0;
        
        // Стоимость работы
        const laborTotalCost = includeLabor ? parseFloatOrDefault(laborCost) * totalVolume : 0;
        
        // Общая стоимость
        const totalCost = concreteCost + deliveryCost + pumpCost + laborTotalCost;
        
        // Расчет необходимого количества машин
        const trucksNeeded = Math.ceil(totalVolume / deliveryTruckCapacity);
        
        // Расчет времени заливки (примерно 30 минут на машину при ручной заливке, 15 минут при использовании насоса)
        const pourTimePerTruck = includePump ? 15 : 30; // минут
        const totalPourTimeMinutes = pourTimePerTruck * trucksNeeded;
        
        // Рекомендации в зависимости от объема и типа конструкции
        let recommendations = [];
        
        // Базовые рекомендации в зависимости от проекта
        if (projectType === 'foundation') {
          recommendations.push('Для фундамента рекомендуется использовать бетон не ниже марки М200');
          recommendations.push('Обязательно используйте гидроизоляцию для защиты фундамента от влаги');
          recommendations.push('При заливке фундамента нужно уделить внимание уплотнению бетона');
        } else if (projectType === 'floor') {
          recommendations.push('Для пола рекомендуется использовать бетон марки М200-М250');
          recommendations.push('Не забудьте про гидроизоляцию и армирование сеткой');
        } else if (projectType === 'wall') {
          recommendations.push('Для стен рекомендуется использовать бетон марки М250-М300');
          recommendations.push('Важно использовать качественную опалубку и вибрирование бетона');
        }
        
        // Рекомендации по объему
        if (totalVolume < 1) {
          recommendations.push('Для небольшого объема можно использовать готовую смесь в мешках');
        } else if (totalVolume > 20) {
          recommendations.push('При большом объеме работ рекомендуется использовать бетононасос');
        }
        
        // Рекомендации по армированию
        if (reinforcementType === 'mesh') {
          recommendations.push('При использовании арматурной сетки важно обеспечить достаточное перекрытие между элементами');
        } else if (reinforcementType === 'bars') {
          recommendations.push('При использовании арматурного каркаса следует обеспечить правильную вязку и нахлест арматуры');
        } else if (reinforcementType === 'fiber') {
          recommendations.push('Фиброволокно равномерно распределяется по всему объему бетона, улучшая его прочность на растяжение');
        }
        
        // Подготовка результатов расчета
        const calculationResults = {
          baseVolume: baseVolume,
          wastageVolume: totalVolume - baseVolume,
          totalVolume: totalVolume,
          concreteWeight: concreteWeight,
          concreteCost: concreteCost,
          deliveryCost: deliveryCost,
          pumpCost: pumpCost,
          laborCost: laborTotalCost,
          totalCost: totalCost,
          pricePerCubicMeter: totalVolume > 0 ? totalCost / totalVolume : 0,
          trucksNeeded: trucksNeeded,
          totalPourTimeMinutes: totalPourTimeMinutes,
          recommendations: recommendations,
          concreteGrade: concreteGrade,
          reinforcementType: reinforcementType,
          projectType: projectType,
          region: selectedRegion.name,
          shapeType: shapeType
        };
        
        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, regions, concretePriceBase]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      shapeType: 'rectangle',
      length: '5',  // метры
      width: '5',   // метры
      height: '0.15', // метры
      diameter: '3',  // метры для цилиндра
      thickness: '0.15', // метры для плиты
      radius: '1.5',  // метры для цилиндра
      lShapePrimaryLength: '5', // метры для L-образной формы
      lShapePrimaryWidth: '3', // метры для L-образной формы
      lShapeSecondaryLength: '2', // метры для L-образной формы
      lShapeSecondaryWidth: '2', // метры для L-образной формы
      customSections: [{
        length: '1',
        width: '1',
        height: '0.15',
        quantity: 1
      }],
      concreteGrade: 'M200', // марка бетона
      reinforcementType: 'mesh', // mesh, bars, fiber, none
      reinforcementDensity: 'standard', // light, standard, heavy
      includeWastage: true,
      wastagePercent: '10',
      projectType: 'foundation', // foundation, floor, wall, etc.
      deliveryDistance: '15', // километры
      regionPrice: 'moscow', // москва, спб, и т.д.
      includeDelivery: true,
      includePump: false,
      includeLabor: false,
      laborCost: '1500' // рублей за кубометр
    });
    setResults(null);
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки и данные
    const headers = [
      'Параметр', 'Значение'
    ];
    
    const rows = [
      ['Тип конструкции', projectTypes.find(t => t.id === formData.projectType)?.name || formData.projectType],
      ['Марка бетона', concreteGrades.find(g => g.id === formData.concreteGrade)?.name || formData.concreteGrade],
      ['Тип армирования', reinforcementTypes.find(r => r.id === formData.reinforcementType)?.name || formData.reinforcementType],
      ['Регион', regions.find(r => r.id === formData.regionPrice)?.name || formData.regionPrice],
      [''],
      ['Базовый объем бетона, м³', results.baseVolume.toFixed(2)],
      ['Запас на отходы, м³', results.wastageVolume.toFixed(2)],
      ['Итоговый объем бетона, м³', results.totalVolume.toFixed(2)],
      ['Вес бетона, кг', results.concreteWeight.toFixed(0)],
      [''],
      ['Стоимость бетона, руб', Math.round(results.concreteCost).toLocaleString()],
      ['Стоимость доставки, руб', Math.round(results.deliveryCost).toLocaleString()],
      formData.includePump ? ['Стоимость насоса, руб', Math.round(results.pumpCost).toLocaleString()] : null,
      formData.includeLabor ? ['Стоимость работ, руб', Math.round(results.laborCost).toLocaleString()] : null,
      ['Общая стоимость, руб', Math.round(results.totalCost).toLocaleString()],
      ['Цена за 1 м³, руб', Math.round(results.pricePerCubicMeter).toLocaleString()],
      [''],
      ['Количество машин', results.trucksNeeded],
      ['Примерное время заливки, мин', results.totalPourTimeMinutes],
      [''],
      ['Рекомендации:']
    ].filter(Boolean);

    // Добавляем рекомендации
    results.recommendations.forEach(rec => {
      rows.push([rec]);
    });

    // Форматируем в CSV
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_бетона.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateConcreteVolume();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Calculator className="w-6 h-6 text-cyan-500" />
          Параметры конструкции
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Выбор формы бетонной конструкции */}
        <ShapeSelectionSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Параметры для прямоугольной формы */}
        {formData.shapeType === 'rectangle' && (
          <RectangleSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Параметры для цилиндрической формы */}
        {formData.shapeType === 'cylinder' && (
          <CylinderSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Параметры для L-образной формы */}
        {formData.shapeType === 'l_shape' && (
          <LShapeSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Параметры для произвольной формы */}
        {formData.shapeType === 'arbitrary' && (
          <ArbitraryShapeSection 
            formData={formData}
            customSections={formData.customSections}
            handleSectionChange={handleSectionChange}
            handleAddSection={handleAddSection}
            handleRemoveSection={handleRemoveSection}
            errors={errors}
          />
        )}

        {/* Выбор материалов и типа проекта */}
        <MaterialSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectTypes={projectTypes}
          concreteGrades={concreteGrades}
          reinforcementTypes={reinforcementTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          regions={regions}
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать объем бетона'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить проект
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;